import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { speedFeatures, speedSportsChannels } from "../../mocks/speedSports";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const speedSportsJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "스피드 스포츠중계",
  description: "가장 빠른 전환 속도로 실시간 스포츠중계를 제공합니다. 끊김 없는 초고속 스트리밍으로 실제 경기와 1초 미만의 지연 시간을 경험하세요.",
  about: {
    "@type": "Service",
    name: "스피드 스포츠중계",
    provider: {
      "@type": "Organization",
      name: "할매티비",
      url: siteUrl,
    },
    description: "초고속 스포츠 실시간 중계 서비스",
  },
};

export default function SpeedSports() {
  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="스피드 스포츠중계 | 초고속 실시간 중계 - 할매티비"
        description="스피드 스포츠중계로 3초 이내 화면 전환, 1초 미만 지연 시간의 끊김 없는 초고속 실시간 중계를 경험하세요. 전 세계 CDN 서버로 어디서나 동일한 속도를 제공합니다."
        keywords="스피드스포츠중계,초고속중계,실시간중계,빠른중계,끊김없는중계,저지연중계"
        canonicalPath="/speed-sports"
        jsonLd={speedSportsJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              스피드 스포츠중계
            </h1>
            <p className="text-dark-text-muted text-base">
              가장 빠른 전환 속도로 실시간 스포츠중계를 제공합니다.
            </p>
          </div>

          {/* Hero Banner */}
          <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8 mb-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-brand-orange/10 rounded-full border border-brand-orange/30">
              <i className="ri-flashlight-line text-brand-orange text-3xl"></i>
            </div>
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">
              3초 이내 화면 전환
            </h2>
            <p className="text-dark-text-muted text-sm max-w-lg mx-auto mb-6">
              스피드 스포츠중계는 끊김 없는 초고속 스트리밍으로 실제 경기와 1초 미만의 지연 시간을 제공합니다.
            </p>
            <Link
              to="/sports-schedule"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-xl"></i>
              <span>지금 바로 시청하기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {speedFeatures.map((feature, i) => (
              <div
                key={i}
                className="bg-dark-surface border border-dark-border rounded-xl p-5 hover:border-brand-orange transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center mb-3 bg-brand-orange/10 rounded-lg">
                  <i className={`${feature.icon} text-brand-orange text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-sm mb-2">{feature.title}</h3>
                <p className="text-dark-text-muted text-xs leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Channel List */}
          <div className="mb-12">
            <h2 className="text-white font-bold text-xl mb-6">스피드 스포츠중계 채널</h2>
            <div className="space-y-3">
              {speedSportsChannels.map((ch, i) => (
                <div
                  key={i}
                  className="bg-dark-surface border border-dark-border rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:border-brand-orange transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-brand-orange/10 rounded-lg">
                      <i className="ri-live-line text-brand-orange text-lg"></i>
                    </div>
                    <div>
                      <span className="text-white font-bold text-sm block">{ch.name}</span>
                      <span className="text-dark-text-muted text-xs">{ch.league}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-dark-text-muted text-xs bg-dark-bg px-3 py-1 rounded-full">
                      {ch.quality}
                    </span>
                    <span className="text-brand-orange text-xs font-bold bg-brand-orange/10 px-3 py-1 rounded-full">
                      지연 {ch.delay}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">스피드 스포츠중계 서비스 안내</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">스피드 스포츠중계</strong>는 실시간 경기 시청의 핵심인 속도를 극대화한 서비스입니다. 일반 중계보다 50% 이상 빠른 로딩 속도로, 클릭 후 3초 이내에 경기 화면이 나타납니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">할매티비</strong>의 <strong className="text-white">스피드 스포츠중계</strong>는 전 세계 CDN 서버를 활용하여 지리적 위치와 관계없이 동일한 속도를 제공합니다. 특히 해외 리그의 경우 일반 중계보다 훨씬 빠른 접속이 가능합니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">스포츠중계</strong>의 가장 중요한 요소는 속도입니다. 중요한 순간을 놓치지 않으려면 스피드 스포츠중계를 선택하세요. 실시간 배당 변화와 경기 흐름을 가장 빠르게 파악할 수 있습니다.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}